<script setup>
import { WdsIcon, WdsPillSelect, WdsPopup, WdsSheet } from '@wds/components';

const props = defineProps({
  propertyTypeObj: {
    type: Object,
    required: true,
  },
  propertyLocation: {
    type: Object,
    required: true,
  },
  applySheet: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(['closePropertyLocation']);

const { isSmallOrMediumScreen } = useMq();

const hasValidLocation = computed(() => {
  return (
    props.propertyLocation
    && props.propertyLocation.geoCoordinates
    && typeof props.propertyLocation.geoCoordinates.longitude === 'number'
    && typeof props.propertyLocation.geoCoordinates.latitude === 'number'
  );
});

const hasFullLocationData = computed(() => {
  return (
    hasValidLocation.value
    && props.propertyLocation.address
    && props.propertyLocation.city
    && props.propertyLocation.country
    && props.propertyLocation.cityCenterDistance !== undefined
  );
});

// DATA
const showDirections = ref(false);

// METHODS
function closePropertyLocationWrapper() {
  emit('closePropertyLocation', 'location');
}

// COMPUTED
const getMapConfigs = computed(() => {
  if (!hasValidLocation.value) {
    return {
      scrollZoom: false,
      center: [0, 0],
      zoom: 1,
    };
  }

  const { geoCoordinates } = props.propertyLocation;
  return {
    scrollZoom: false,
    center: [geoCoordinates?.longitude || 0, geoCoordinates?.latitude || 0],
    zoom: 16,
  };
});

const getMapMakersList = computed(() => {
  if (!hasValidLocation.value || !props.propertyLocation.geoCoordinates) {
    return [];
  }

  const { geoCoordinates } = props.propertyLocation;
  return [{ ...geoCoordinates }];
});

const propertyAddress = computed(() => {
  if (!hasValidLocation.value || !props.propertyLocation?.address) {
    return '';
  }

  const {
    line1,
    line2,
  } = props.propertyLocation.address;
  return [line1, line2].filter((l) => l).join(', ');
});
</script>

<template>
  <WdsPopup
    is-open
    :apply-sheet="applySheet"
    :heading="$t('t_LOCATION')"
    close-button
    @popup-close="closePropertyLocationWrapper"
  >
    <template #popupContent>
      <div
        :class="{ mobile: isSmallOrMediumScreen }"
        class="property-location-container"
      >
        <div
          v-if="hasFullLocationData && showDirections && !isSmallOrMediumScreen"
          class="direction-container"
        >
          <div class="direction-details-container">
            <div class="address-container">
              <WdsIcon asset="map-pin" />
              <div class="address-container-content">
                {{ `${propertyAddress}${propertyLocation?.city?.name && ','}` }}
                {{ `${propertyLocation?.city?.name}${propertyLocation?.country?.name && ','}` }}
                {{ propertyLocation?.country?.name }}
              </div>
            </div>
            <div class="distance-container">
              <WdsIcon asset="hostel" />
              <div class="distance-container-content">
                {{ $t('t_FROMCITYCENTER', { DISTANCE: propertyLocation?.cityCenterDistance }) }}
              </div>
            </div>
          </div>
        </div>

        <div class="map-overlay">
          <CommonHwMap
            v-if="hasValidLocation"
            :property-type-obj="propertyTypeObj"
            :map-config="getMapConfigs"
            :marker-list="getMapMakersList"
            :apply-clusters="false"
            :auto-fit-bounds="false"
            :map-view-visible="false"
          >
            <WdsPillSelect
              v-if="hasFullLocationData"
              :class="{ mobile: isSmallOrMediumScreen }"
              :text="$t('t_TITLEDIRECTIONS')"
              aria-label="Directions button"
              class="direction-pill"
              icon-start="directions"
              @click="showDirections = !showDirections"
            />
          </CommonHwMap>
        </div>
      </div>
    </template>
  </WdsPopup>

  <WdsSheet
    v-if="isSmallOrMediumScreen && showDirections"
    is-open
    :head-divider="false"
    :sheet-height="40"
    close-button
    close-button-aria-label="Close"
    @sheet-close="showDirections = false"
  >
    <template #sheetHeader>
      <div class="title-4-bld">
        {{ $t('t_TITLEDIRECTIONS') }}
      </div>
    </template>
    <template #sheetBody>
      <div
        v-if="hasFullLocationData"
        class="direction-container mobile"
      >
        <div class="direction-details-container">
          <div class="address-container">
            <WdsIcon asset="map-pin" />
            <div class="address-container-content mobile">
              {{ `${propertyAddress}${propertyLocation?.city?.name && ','}` }}
              {{ `${propertyLocation?.city?.name}${propertyLocation?.country?.name && ','}` }}
              {{ propertyLocation?.country?.name }}
            </div>
          </div>
          <div class="distance-container">
            <WdsIcon asset="hostel" />
            <div class="distance-container-content">
              {{ $t('t_FROMCITYCENTER', { DISTANCE: propertyLocation?.cityCenterDistance }) }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </WdsSheet>
</template>

<style lang="scss" scoped>
:deep(.sheet-body) {
  height: 100%;
  padding: 0 !important;
}

@media screen and (width >= 1024px) {
  :deep(.modal-container) {
    max-width: calc(100% - 6rem) !important;
    max-height: calc(100% - 6rem) !important;
  }
}

.property-location-container {
  display: flex;
  padding-bottom: $wds-spacing-l;
  width: calc(100vw - 9rem);
  height: calc(100vh - 10rem);

  &.mobile {
    padding: 0;
    height: 100%;
    width: 100%;
    max-width: unset;
    max-height: unset;
    position: relative;
    overflow-y: hidden;

    :deep(.container) {
      border-radius: 0;
    }
  }
}

.direction-container {
  flex: 0 0 25%;
  margin-right: $wds-spacing-m;

  &.mobile {
    padding: 0 $wds-spacing-m $wds-spacing-m;
    margin-right: 0;
    max-height: wds-rem(350px);
  }

  .address-container {
    display: flex;
    align-items: center;
    margin-bottom: $wds-spacing-s;

    :deep(.icon-container) {
      margin-right: $wds-spacing-s;
    }
  }

  .distance-container {
    display: flex;
    align-items: center;
    margin-bottom: $wds-spacing-s;

    :deep(.icon-container) {
      margin-right: $wds-spacing-s;
    }
  }
}

.map-overlay {
  width: 100%;
  height: 100%;
  position: relative;
  min-height: wds-rem(400px);

  :deep(.pin-wrapper) {
    .pin-marker::before {
      background-color: $wds-color-purple;
    }
  }

  :deep(.map-container) {
    height: 100%;
    width: 100%;
  }

  :deep(.map-canvas) {
    height: 100%;
    width: 100%;
  }
}

.direction-pill {
  position: absolute;
  top: $wds-spacing-m;
  left: $wds-spacing-m;
  z-index: 2;

  &.mobile {
    inset: auto $wds-spacing-m $wds-spacing-m auto;
  }
}
</style>
